import React, { useState, useEffect } from 'react';
import { Check, Zap, TrendingUp, Crown, Building2, ArrowRight, X } from 'lucide-react';
import { useAuth } from '@clerk/clerk-react';
import { useToast } from './Toast';
import { useTheme } from '../context/ThemeContext';

const API_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:3001';

const PLANS = [
  {
    id: 'starter',
    name: 'Starter',
    price: 99,
    icon: Zap,
    color: '#3b82f6',
    adSpendRange: 'Up to €5K',
    adSpendDetail: 'Up to €5,000 monthly ad spend',
    description: 'Perfect for small businesses getting started with Google Ads',
    features: [
      'Up to 10 Google Ads accounts',
      '300 optimizations per month',
      '150 campaigns per month',
      'AI Bid Optimizer',
      'Negative keywords manager',
      'Ad copy generator',
      'Basic keyword research',
      'Email support',
      '7-day free trial'
    ],
    limits: {
      accounts: 10,
      optimizations: 300,
      campaigns: 150
    }
  },
  {
    id: 'growth',
    name: 'Growth',
    price: 249,
    icon: TrendingUp,
    color: '#8b5cf6',
    adSpendRange: 'Up to €20K',
    adSpendDetail: 'Up to €20,000 monthly ad spend',
    description: 'Ideal for growing businesses scaling their ad campaigns',
    popular: true,
    features: [
      'Up to 25 Google Ads accounts',
      '1,000 optimizations per month',
      '500 campaigns per month',
      'Everything in Starter, plus:',
      'Offer Intelligence',
      'Multi-language support',
      'A/B Testing Lab',
      'Quality Score Optimizer',
      'Priority email support',
      '7-day free trial'
    ],
    limits: {
      accounts: 25,
      optimizations: 1000,
      campaigns: 500
    }
  },
  {
    id: 'scale',
    name: 'Scale',
    price: 399,
    icon: Crown,
    color: '#10b981',
    adSpendRange: '€20K - €100K',
    adSpendDetail: '€20,000 - €100,000 monthly ad spend',
    description: 'For agencies managing multiple high-value clients',
    popular: false,
    features: [
      'Up to 50 Google Ads accounts',
      '5,000 optimizations per month',
      '2,000 campaigns per month',
      'Everything in Growth, plus:',
      'Server-side tracking',
      'Competitor analysis',
      'Priority phone & chat support',
      'Custom integrations',
      'Team collaboration tools',
      '7-day free trial'
    ],
    limits: {
      accounts: 50,
      optimizations: 5000,
      campaigns: 2000
    }
  },
  {
    id: 'enterprise',
    name: 'Enterprise',
    price: 799,
    icon: Building2,
    color: '#f59e0b',
    adSpendRange: '€100K+',
    adSpendDetail: '€100,000+ monthly ad spend (unlimited)',
    description: 'For large enterprises and agencies requiring maximum scale',
    popular: false,
    features: [
      'Unlimited Google Ads accounts',
      'Unlimited optimizations',
      'Unlimited campaigns',
      'Everything in Scale, plus:',
      'White-label option',
      'Dedicated account manager',
      'Custom SLA & support',
      'API access',
      'Advanced security features',
      'Custom onboarding & training',
      '7-day free trial'
    ],
    limits: {
      accounts: 'Unlimited',
      optimizations: 'Unlimited',
      campaigns: 'Unlimited'
    }
  }
];

export default function SubscriptionPlans({ onClose }) {
  const { getToken } = useAuth();
  const { showError, showWarning } = useToast();
  const { theme } = useTheme();
  const isLight = theme.mode === 'light';
  const [currentPlan, setCurrentPlan] = useState(null);
  const [userInfo, setUserInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [billingCycle, setBillingCycle] = useState('monthly'); // 'monthly' or 'yearly'

  // Theme-aware color tokens used throughout this component.
  // We keep the dark-mode look identical to before; the light-mode values are
  // chosen to ensure proper contrast on a white/grey backdrop.
  const t = {
    overlay: isLight ? 'rgba(15, 23, 42, 0.45)' : 'rgba(0, 0, 0, 0.75)',
    modalBg: theme.colors.bg.secondary,
    modalBorder: theme.colors.border.primary,
    modalShadow: isLight
      ? '0 20px 60px rgba(15, 23, 42, 0.18)'
      : '0 20px 60px rgba(0, 0, 0, 0.5)',
    text: theme.colors.text.primary,
    textSecondary: theme.colors.text.secondary,
    textTertiary: theme.colors.text.tertiary,
    textMuted: theme.colors.text.muted,
    border: theme.colors.border.primary,
    cardBg: isLight ? '#ffffff' : 'rgba(255, 255, 255, 0.05)',
    cardBorder: theme.colors.border.primary,
    cardShadow: isLight
      ? '0 1px 3px rgba(15, 23, 42, 0.08), 0 4px 12px rgba(15, 23, 42, 0.06)'
      : '0 4px 20px rgba(0, 0, 0, 0.2)',
    popularCardBg: isLight
      ? 'linear-gradient(135deg, rgba(16, 185, 129, 0.10) 0%, rgba(5, 150, 105, 0.05) 100%)'
      : 'linear-gradient(135deg, rgba(16, 185, 129, 0.15) 0%, rgba(5, 150, 105, 0.1) 100%)',
    popularCardShadow: isLight
      ? '0 10px 30px rgba(16, 185, 129, 0.15)'
      : '0 10px 30px rgba(16, 185, 129, 0.2)',
    bannerBg: isLight
      ? 'linear-gradient(135deg, rgba(16, 185, 129, 0.12) 0%, rgba(5, 150, 105, 0.06) 100%)'
      : 'linear-gradient(135deg, rgba(16, 185, 129, 0.15) 0%, rgba(5, 150, 105, 0.1) 100%)',
    bannerBorder: 'rgba(16, 185, 129, 0.35)',
    priceBadgeBg: isLight ? 'rgba(16, 185, 129, 0.12)' : 'rgba(16, 185, 129, 0.2)',
    priceBadgeBorder: 'rgba(16, 185, 129, 0.35)',
    closeBg: isLight ? 'rgba(15, 23, 42, 0.06)' : 'rgba(255, 255, 255, 0.1)',
    closeBgHover: isLight ? 'rgba(15, 23, 42, 0.12)' : 'rgba(255, 255, 255, 0.2)',
    closeIcon: isLight ? '#1d1d1f' : '#ffffff',
    toggleOff: isLight ? 'rgba(15, 23, 42, 0.15)' : 'rgba(255, 255, 255, 0.2)',
    faqBg: isLight ? '#ffffff' : 'rgba(255, 255, 255, 0.05)',
    helpBg: isLight ? 'rgba(99, 102, 241, 0.08)' : 'rgba(99, 102, 241, 0.1)',
    helpBorder: isLight ? 'rgba(99, 102, 241, 0.25)' : 'rgba(99, 102, 241, 0.2)',
    trialPillBg: isLight ? 'rgba(251, 191, 36, 0.18)' : 'rgba(251, 191, 36, 0.15)',
    trialPillBorder: 'rgba(251, 191, 36, 0.45)',
    trialPillText: isLight ? '#92400e' : '#fbbf24',
    success: '#10b981'
  };
  
  // Calculate yearly price with 10% discount
  const getYearlyTotal = (plan) => {
    const yearlyTotal = plan.price * 12;
    const discount = yearlyTotal * 0.10; // 10% discount
    return (yearlyTotal - discount).toFixed(2); // Exact yearly price (e.g., €507.60)
  };

  const getMonthlyEquivalent = (plan) => {
    const yearlyTotal = plan.price * 12;
    const discounted = yearlyTotal * 0.90;
    return (discounted / 12).toFixed(2); // Monthly equivalent (e.g., €42.30)
  };
  
  const getYearlySavings = (plan) => {
    const yearlyTotal = plan.price * 12;
    return (yearlyTotal * 0.10).toFixed(2); // 10% savings
  };

  useEffect(() => {
    fetchCurrentPlan();
  }, []);

  const fetchCurrentPlan = async () => {
    try {
      const token = await getToken();
      const response = await fetch(`${API_URL}/api/user/me`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      const data = await response.json();
      if (data.success) {
        setCurrentPlan(data.subscription.tier);
        setUserInfo(data);
      }
    } catch (error) {
      console.error('Error fetching current plan:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleUpgrade = async (planId) => {
    try {
      // Get the Stripe Price ID for the selected plan based on billing cycle
      const monthlyPriceIds = {
        starter: process.env.REACT_APP_STRIPE_PRICE_ID_STARTER,
        growth: process.env.REACT_APP_STRIPE_PRICE_ID_GROWTH,
        scale: process.env.REACT_APP_STRIPE_PRICE_ID_SCALE,
        enterprise: process.env.REACT_APP_STRIPE_PRICE_ID_ENTERPRISE
      };
      
      const yearlyPriceIds = {
        starter: process.env.REACT_APP_STRIPE_PRICE_ID_STARTER_YEARLY,
        growth: process.env.REACT_APP_STRIPE_PRICE_ID_GROWTH_YEARLY,
        scale: process.env.REACT_APP_STRIPE_PRICE_ID_SCALE_YEARLY,
        enterprise: process.env.REACT_APP_STRIPE_PRICE_ID_ENTERPRISE_YEARLY
      };

      const priceId = billingCycle === 'yearly' 
        ? yearlyPriceIds[planId] 
        : monthlyPriceIds[planId];
      
      if (!priceId) {
        showWarning('Invalid plan selected');
        return;
      }

      console.log('Creating checkout session for:', planId, priceId);

      // Get auth token
      const token = await getToken();

      // Call backend to create checkout session
      const response = await fetch(`${API_URL}/api/billing/create-checkout`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ priceId })
      });

      const data = await response.json();

      // Partner accounts are billed per client via the Partner Dashboard and
      // cannot buy a self-serve plan — the backend blocks this at the API.
      if (response.status === 403 && data.code === 'PARTNER_SELF_SERVE_BLOCKED') {
        showWarning('Partner accounts worden per klant afgerekend in het Partner Dashboard. Je kunt geen self-serve plan kopen.');
        if (data.redirect) {
          setTimeout(() => { window.location.href = data.redirect; }, 1500);
        }
        return;
      }

      if (data.url) {
        // Redirect to Stripe Checkout
        window.location.href = data.url;
      } else {
        showError('Failed to create checkout session. Please try again.');
      }
    } catch (error) {
      console.error('Checkout error:', error);
      showError('Something went wrong. Please try again.');
    }
  };

  const isCurrentPlan = (planId) => {
    if (currentPlan === 'trial') return false;
    return currentPlan === planId;
  };

  const canUpgrade = (planId) => {
    const planOrder = { trial: 0, starter: 1, growth: 2, scale: 3, enterprise: 4 };
    const currentLevel = planOrder[currentPlan] || 0;
    const targetLevel = planOrder[planId] || 0;
    return targetLevel > currentLevel;
  };

  return (
    <>
      {/* Modal Overlay */}
      <div 
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: t.overlay,
          zIndex: 1000,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '20px',
          overflowY: 'auto'
        }}
        onClick={onClose}
      >
        {/* Modal Content */}
        <div 
          style={{
            background: t.modalBg,
            borderRadius: '24px',
            maxWidth: '1400px',
            width: '100%',
            maxHeight: '90vh',
            overflowY: 'auto',
            position: 'relative',
            boxShadow: t.modalShadow,
            border: `1px solid ${t.modalBorder}`
          }}
          onClick={(e) => e.stopPropagation()}
        >
          {/* Close Button */}
          <button
            onClick={onClose}
            style={{
              position: 'absolute',
              top: '20px',
              right: '20px',
              background: t.closeBg,
              border: 'none',
              borderRadius: '50%',
              width: '40px',
              height: '40px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
              transition: 'all 0.2s',
              zIndex: 10
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = t.closeBgHover;
              e.currentTarget.style.transform = 'rotate(90deg)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = t.closeBg;
              e.currentTarget.style.transform = 'rotate(0deg)';
            }}
          >
            <X size={20} style={{ color: t.closeIcon }} />
          </button>

          {/* Content */}
          <div style={{ padding: '60px 40px 40px' }}>
        
        {/* Current Subscription Banner (if user has a plan) */}
        {!loading && currentPlan && currentPlan !== 'trial' && userInfo && (
          <div style={{
            background: t.bannerBg,
            border: `1px solid ${t.bannerBorder}`,
            borderRadius: '16px',
            padding: '32px 40px',
            marginBottom: '40px',
            boxShadow: '0 4px 20px rgba(16, 185, 129, 0.1)'
          }}>
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'flex-start'
            }}>
              <div style={{ flex: 1 }}>
                <div style={{
                  fontSize: '0.75rem',
                  fontWeight: '700',
                  color: t.success,
                  textTransform: 'uppercase',
                  letterSpacing: '1.5px',
                  marginBottom: '12px'
                }}>
                  ✓ CURRENT SUBSCRIPTION
                </div>
                <h3 style={{
                  fontSize: '1.75rem',
                  fontWeight: '700',
                  color: t.text,
                  marginBottom: '8px',
                  margin: 0
                }}>
                  {userInfo.subscription.tierName} Plan
                </h3>
                <p style={{
                  fontSize: '1rem',
                  color: t.textSecondary,
                  lineHeight: '1.6',
                  margin: '12px 0 0 0'
                }}>
                  {PLANS.find(p => p.id === currentPlan)?.adSpendDetail || ''} • {userInfo.subscription.limits.maxAccounts === 999 ? 'Unlimited' : userInfo.subscription.limits.maxAccounts} accounts
                </p>
              </div>
              <div style={{
                background: t.priceBadgeBg,
                borderRadius: '12px',
                padding: '20px 32px',
                marginLeft: '32px',
                minWidth: '180px',
                textAlign: 'center',
                border: `1px solid ${t.priceBadgeBorder}`
              }}>
                <div style={{
                  fontSize: '0.75rem',
                  fontWeight: '600',
                  color: t.textSecondary,
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px',
                  marginBottom: '12px'
                }}>
                  Monthly Cost
                </div>
                <div style={{
                  fontSize: '2.5rem',
                  fontWeight: '700',
                  color: t.success,
                  lineHeight: '1'
                }}>
                  €{PLANS.find(p => p.id === currentPlan)?.price || 0}
                </div>
                <div style={{
                  fontSize: '0.875rem',
                  color: t.textTertiary,
                  marginTop: '8px'
                }}>
                  per month
                </div>
              </div>
            </div>
          </div>
        )}
        
        {/* Header */}
        <div style={{ textAlign: 'center', marginBottom: '50px' }}>
          <h1 style={{
            fontSize: '2.5rem',
            fontWeight: '700',
            marginBottom: '16px',
            color: t.text
          }}>
            {currentPlan && currentPlan !== 'trial' ? 'Manage Your Subscription' : 'Choose Your Plan'}
          </h1>
          <p style={{
            fontSize: '1.125rem',
            color: t.textSecondary,
            maxWidth: '600px',
            margin: '0 auto 16px',
            lineHeight: '1.6'
          }}>
            {currentPlan && currentPlan !== 'trial'
              ? 'Upgrade or downgrade your plan anytime'
              : 'Scale your Google Ads campaigns with AI-powered automation'}
          </p>
          {/* Billing Toggle */}
          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '16px',
            marginBottom: '32px'
          }}>
            <span style={{
              fontSize: '1rem',
              color: billingCycle === 'monthly' ? t.text : t.textTertiary,
              fontWeight: billingCycle === 'monthly' ? '600' : '400'
            }}>
              Monthly
            </span>
            <button
              onClick={() => setBillingCycle(billingCycle === 'monthly' ? 'yearly' : 'monthly')}
              style={{
                width: '56px',
                height: '28px',
                borderRadius: '14px',
                background: billingCycle === 'yearly'
                  ? 'linear-gradient(135deg, #10b981 0%, #059669 100%)'
                  : t.toggleOff,
                border: 'none',
                cursor: 'pointer',
                position: 'relative',
                transition: 'all 0.3s ease'
              }}
            >
              <div style={{
                width: '22px',
                height: '22px',
                borderRadius: '50%',
                background: 'white',
                position: 'absolute',
                top: '3px',
                left: billingCycle === 'yearly' ? '31px' : '3px',
                transition: 'left 0.3s ease',
                boxShadow: '0 2px 4px rgba(0,0,0,0.2)'
              }} />
            </button>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <span style={{
                fontSize: '1rem',
                color: billingCycle === 'yearly' ? t.text : t.textTertiary,
                fontWeight: billingCycle === 'yearly' ? '600' : '400'
              }}>
                Yearly
              </span>
              {billingCycle === 'yearly' && (
                <span style={{
                  background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                  color: 'white',
                  fontSize: '0.75rem',
                  fontWeight: '600',
                  padding: '4px 8px',
                  borderRadius: '12px'
                }}>
                  Save 10%
                </span>
              )}
            </div>
          </div>
          
          {(!currentPlan || currentPlan === 'trial') && (
            <div style={{
              display: 'inline-block',
              background: t.trialPillBg,
              border: `1px solid ${t.trialPillBorder}`,
              borderRadius: '20px',
              padding: '8px 20px',
              fontSize: '0.875rem',
              color: t.trialPillText,
              fontWeight: '600'
            }}>
              🎉 7-day free trial on all plans • No credit card required
            </div>
          )}
        </div>

        {/* Pricing Cards */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
          gap: '24px',
          marginBottom: '60px',
          maxWidth: '1600px',
          margin: '0 auto 60px'
        }}>
          {PLANS.map((plan) => {
            const Icon = plan.icon;
            const isCurrent = isCurrentPlan(plan.id);
            const upgradable = canUpgrade(plan.id);

            return (
              <div
                key={plan.id}
                style={{
                  background: plan.popular ? t.popularCardBg : t.cardBg,
                  border: isCurrent
                    ? '2px solid #10b981'
                    : plan.popular
                      ? '2px solid #10b981'
                      : `1px solid ${t.cardBorder}`,
                  borderRadius: '20px',
                  padding: '40px 28px',
                  position: 'relative',
                  transition: 'all 0.3s',
                  transform: plan.popular ? 'scale(1.03)' : 'scale(1)',
                  boxShadow: plan.popular ? t.popularCardShadow : t.cardShadow
                }}
                onMouseEnter={(e) => {
                  if (!isCurrent && !plan.popular) {
                    e.currentTarget.style.transform = 'translateY(-5px)';
                    e.currentTarget.style.borderColor = plan.color;
                  }
                }}
                onMouseLeave={(e) => {
                  if (!isCurrent && !plan.popular) {
                    e.currentTarget.style.transform = 'scale(1)';
                    e.currentTarget.style.borderColor = t.cardBorder;
                  }
                }}
              >
                {/* Most Popular Badge - only show for new users (trial or no plan) */}
                {plan.popular && (!currentPlan || currentPlan === 'trial') && (
                  <div style={{
                    position: 'absolute',
                    top: '-12px',
                    left: '50%',
                    transform: 'translateX(-50%)',
                    background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                    color: 'white',
                    padding: '6px 20px',
                    borderRadius: '20px',
                    fontSize: '12px',
                    fontWeight: '700',
                    textTransform: 'uppercase',
                    letterSpacing: '1px',
                    boxShadow: '0 4px 12px rgba(16, 185, 129, 0.4)'
                  }}>
                    ⭐ Most Popular
                  </div>
                )}

                {/* Current Plan Badge */}
                {isCurrent && (
                  <div style={{
                    position: 'absolute',
                    top: '-12px',
                    left: '50%',
                    transform: 'translateX(-50%)',
                    background: '#10b981',
                    color: 'white',
                    padding: '6px 20px',
                    borderRadius: '20px',
                    fontSize: '12px',
                    fontWeight: '700',
                    textTransform: 'uppercase',
                    letterSpacing: '1px',
                    boxShadow: '0 4px 12px rgba(16, 185, 129, 0.4)'
                  }}>
                    ✓ Current Plan
                  </div>
                )}

                {/* Plan Badge */}
                <div style={{
                  background: `${plan.color}20`,
                  color: plan.color,
                  padding: '6px 16px',
                  borderRadius: '20px',
                  fontSize: '0.75rem',
                  fontWeight: '700',
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px',
                  marginBottom: '20px',
                  display: 'inline-block'
                }}>
                  {plan.name}
                </div>

                {/* Price */}
                <div style={{ marginBottom: '12px' }}>
                  <span style={{
                    fontSize: '3rem',
                    fontWeight: '700',
                    color: t.text
                  }}>
                    €{billingCycle === 'yearly' ? getMonthlyEquivalent(plan) : plan.price}
                  </span>
                  <span style={{
                    fontSize: '1rem',
                    color: t.textTertiary,
                    marginLeft: '4px'
                  }}>
                    /month
                  </span>
                  {billingCycle === 'yearly' && (
                    <div style={{
                      fontSize: '0.8rem',
                      color: t.success,
                      marginTop: '4px'
                    }}>
                      €{getYearlyTotal(plan)}/year (save €{getYearlySavings(plan)})
                    </div>
                  )}
                </div>

                {/* Ad Spend Range */}
                <div style={{
                  fontSize: '0.875rem',
                  color: t.textSecondary,
                  marginBottom: '24px',
                  paddingBottom: '24px',
                  borderBottom: `1px solid ${t.border}`
                }}>
                  {plan.adSpendDetail}
                </div>

                {/* Features List */}
                <div style={{ marginBottom: '24px' }}>
                  {plan.features.map((feature, index) => (
                    <div
                      key={index}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '12px',
                        marginBottom: '12px'
                      }}
                    >
                      <div style={{
                        width: '20px',
                        height: '20px',
                        borderRadius: '50%',
                        background: `${plan.color}30`,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0
                      }}>
                        <Check size={12} style={{ color: plan.color, strokeWidth: 3 }} />
                      </div>
                      <span style={{
                        fontSize: '0.9rem',
                        color: feature.includes('Everything in')
                          ? t.textTertiary
                          : t.textSecondary,
                        fontWeight: feature.includes('Everything in') ? '600' : '400',
                        lineHeight: '1.4'
                      }}>
                        {feature}
                      </span>
                    </div>
                  ))}
                </div>

                {/* CTA Button */}
                <button
                  onClick={() => handleUpgrade(plan.id)}
                  disabled={isCurrent}
                  style={{
                    width: '100%',
                    padding: '14px 24px',
                    borderRadius: '10px',
                    border: 'none',
                    fontSize: '0.95rem',
                    fontWeight: '600',
                    cursor: isCurrent ? 'default' : 'pointer',
                    transition: 'all 0.2s',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '8px',
                    ...(isCurrent ? {
                      background: isLight ? 'rgba(15, 23, 42, 0.06)' : 'rgba(255,255,255,0.1)',
                      color: t.textTertiary
                    } : plan.popular ? {
                      background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                      color: 'white'
                    } : {
                      background: `${plan.color}20`,
                      color: plan.color,
                      border: `2px solid ${plan.color}40`
                    })
                  }}
                  onMouseEnter={(e) => {
                    if (!isCurrent) {
                      e.currentTarget.style.transform = 'translateY(-2px)';
                      e.currentTarget.style.boxShadow = `0 8px 24px ${plan.color}40`;
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (!isCurrent) {
                      e.currentTarget.style.transform = 'translateY(0)';
                      e.currentTarget.style.boxShadow = 'none';
                    }
                  }}
                >
                  {isCurrent ? 'Current Plan' : upgradable ? 'Upgrade Now' : 'Get Started'}
                  {!isCurrent && <ArrowRight size={18} />}
                </button>
              </div>
            );
          })}
        </div>

        {/* FAQ / Additional Info */}
        <div style={{
          background: t.faqBg,
          borderRadius: '20px',
          padding: '40px',
          border: `1px solid ${t.border}`,
          marginTop: '60px',
          boxShadow: isLight ? t.cardShadow : 'none'
        }}>
          <h3 style={{
            fontSize: '1.5rem',
            fontWeight: '700',
            marginBottom: '24px',
            color: t.text
          }}>
            Frequently Asked Questions
          </h3>
          
          <div style={{ display: 'grid', gap: '24px' }}>
            <div>
              <h4 style={{ fontSize: '1.05rem', fontWeight: '600', marginBottom: '8px', color: t.text }}>
                What happens after the free trial?
              </h4>
              <p style={{ fontSize: '0.95rem', color: t.textSecondary, lineHeight: '1.6', margin: 0 }}>
                After your 7-day free trial, you'll be charged for your selected plan. You can cancel anytime before the trial ends without being charged.
              </p>
            </div>

            <div>
              <h4 style={{ fontSize: '1.05rem', fontWeight: '600', marginBottom: '8px', color: t.text }}>
                Can I change plans later?
              </h4>
              <p style={{ fontSize: '0.95rem', color: t.textSecondary, lineHeight: '1.6', margin: 0 }}>
                Yes! You can upgrade or downgrade your plan at any time. Changes take effect immediately, and we'll prorate the difference.
              </p>
            </div>

            <div>
              <h4 style={{ fontSize: '1.05rem', fontWeight: '600', marginBottom: '8px', color: t.text }}>
                What if I exceed my ad spend limit?
              </h4>
              <p style={{ fontSize: '0.95rem', color: t.textSecondary, lineHeight: '1.6', margin: 0 }}>
                We'll automatically suggest an upgrade if you're approaching your limit. Your campaigns will continue running, and you can upgrade anytime.
              </p>
            </div>

            <div>
              <h4 style={{ fontSize: '1.05rem', fontWeight: '600', marginBottom: '8px', color: t.text }}>
                Do you offer refunds?
              </h4>
              <p style={{ fontSize: '0.95rem', color: t.textSecondary, lineHeight: '1.6', margin: 0 }}>
                We offer a 30-day money-back guarantee if you're not satisfied with our service. No questions asked.
              </p>
            </div>
          </div>
        </div>

        {/* Need Help Section */}
        <div style={{
          marginTop: '30px',
          textAlign: 'center',
          padding: '30px',
          background: t.helpBg,
          borderRadius: '16px',
          border: `1px solid ${t.helpBorder}`
        }}>
          <h4 style={{
            fontSize: '1.25rem',
            fontWeight: '600',
            marginBottom: '12px',
            color: t.text
          }}>
            Need help choosing a plan?
          </h4>
          <p style={{
            fontSize: '1rem',
            color: t.textSecondary,
            marginBottom: '20px'
          }}>
            Our team is here to help you find the perfect plan for your business.
          </p>
          <button style={{
            padding: '12px 30px',
            background: 'linear-gradient(135deg, #8b5cf6 0%, #6366f1 100%)',
            color: 'white',
            border: 'none',
            borderRadius: '10px',
            fontSize: '1rem',
            fontWeight: '600',
            cursor: 'pointer',
            transition: 'all 0.3s'
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.transform = 'translateY(-2px)';
            e.currentTarget.style.boxShadow = '0 8px 24px rgba(139, 92, 246, 0.4)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.transform = 'translateY(0)';
            e.currentTarget.style.boxShadow = 'none';
          }}>
            Contact Sales
          </button>
        </div>
          </div>
        </div>
      </div>
    </>
  );
}
